import type React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { analytics } from '@/analytics';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { allowedCageTypes, isSmoltLocation } from '@/utils/locationTypeHelpers';

import type { AddCageModalProps } from './AddCageModal';
import {
    applyCountChange,
    applyNameChange,
    type FormState,
    MAX_COUNT,
    MIN_COUNT,
    makeInitialState,
} from './addCageForm';

type AddCageFormInput = Pick<
    AddCageModalProps,
    'onClose' | 'createManyCages' | 'location' | 'locationId' | 'cageTypesList'
>;

export interface AddCageForm {
    formState: FormState;
    visibleCageTypes: NonNullable<AddCageModalProps['cageTypesList']>;
    isLocked: boolean;
    hasResolvedLockedCageType: boolean;
    isResolvingLockedCageType: boolean;
    selectedCageTypeId: number;
    selectedFishTypeId: number;
    handleFishTypeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    handleCageTypeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    handleCountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleNameChange: (index: number, value: string) => void;
    handleClose: () => void;
    handleSubmit: (e: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
}

/** The add-pens form: its state, the defaults it derives from the location, and submit. */
export function useAddCageForm(input: AddCageFormInput): AddCageForm {
    const { onClose, createManyCages, location, locationId, cageTypesList = [] } = input;
    const { t } = useTranslation('clientLocations');

    const defaultPrefix = t('addCageModal.defaultNamePrefix');
    const [formState, setFormState] = useState<FormState>(() => makeInitialState(defaultPrefix));

    // Default to the location's species. A location can hold several (WB-2207), so this is a
    // default rather than a constraint, and the common case needs no interaction.
    const selectedFishTypeId =
        formState.fishTypeId === -1 ? (location?.fishtypeid ?? -1) : formState.fishTypeId;

    const handleFishTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setFormState((prev) => ({
            ...prev,
            fishTypeId: Number.parseInt(e.target.value, 10),
            fishTypeError: '',
        }));
    }, []);

    const visibleCageTypes = allowedCageTypes(cageTypesList, location?.location_type);
    const isLocked = isSmoltLocation(location?.location_type);
    const hasResolvedLockedCageType = !isLocked || visibleCageTypes.length > 0;
    const isResolvingLockedCageType = isLocked && !hasResolvedLockedCageType;
    const selectedCageTypeId =
        isLocked && hasResolvedLockedCageType ? visibleCageTypes[0].id : formState.cageTypeId;

    const handleCageTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setFormState((prev) => ({
            ...prev,
            cageTypeId: Number.parseInt(e.target.value, 10),
            cageTypeError: '',
        }));
    }, []);

    const handleCountChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const raw = e.target.value;
            setFormState((prev) => applyCountChange(prev, raw, defaultPrefix));
        },
        [defaultPrefix]
    );

    const handleNameChange = useCallback((index: number, value: string) => {
        setFormState((prev) => applyNameChange(prev, index, value));
    }, []);

    const handleClose = useCallback(() => {
        setFormState(makeInitialState(defaultPrefix));
        onClose();
    }, [onClose, defaultPrefix]);

    const handleSubmit = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();

            if (locationId === undefined || !hasResolvedLockedCageType) {
                return;
            }

            if (!isLocked && selectedCageTypeId === -1) {
                setFormState((prev) => ({
                    ...prev,
                    cageTypeError: t('addCageModal.chooseCageType'),
                }));
                return;
            }

            if (selectedFishTypeId === -1) {
                setFormState((prev) => ({
                    ...prev,
                    fishTypeError: t('addCageModal.chooseFishType'),
                }));
                return;
            }

            // Validate count — catches empty field or out-of-range values
            const parsedCount = Number.parseInt(formState.countRaw, 10);
            if (Number.isNaN(parsedCount) || parsedCount < MIN_COUNT || parsedCount > MAX_COUNT) {
                setFormState((prev) => ({
                    ...prev,
                    countError: t('addCageModal.countRequired'),
                }));
                return;
            }

            // All displayed fields must be filled — a blank field means the user's
            // declared count diverges from the actual submission.
            const hasBlankFields = formState.names.some((f) => f.value.trim().length === 0);
            if (hasBlankFields) {
                setFormState((prev) => ({
                    ...prev,
                    names: prev.names.map((f) =>
                        f.value.trim().length === 0
                            ? { ...f, error: t('addCageModal.nameRequired') }
                            : f
                    ),
                }));
                return;
            }

            const filledNames = formState.names.map((f) => f.value.trim());

            // Warn on duplicate names
            const uniqueNames = new Set(filledNames);
            if (uniqueNames.size < filledNames.length) {
                setFormState((prev) => ({
                    ...prev,
                    systemError: t('addCageModal.duplicateNames'),
                }));
                return;
            }

            setFormState((prev) => ({ ...prev, saving: true, systemError: '' }));

            try {
                await createManyCages(
                    locationId,
                    selectedCageTypeId,
                    selectedFishTypeId,
                    filledNames
                );
                analytics.featureActionUsed({ featureName: 'creating_pens' });
                setFormState(makeInitialState(defaultPrefix));
                onClose();
            } catch (error: unknown) {
                const systemError = apiErrorContent(error);
                setFormState((prev) => ({ ...prev, saving: false, systemError }));
            }
        },
        [
            createManyCages,
            defaultPrefix,
            locationId,
            formState,
            hasResolvedLockedCageType,
            isLocked,
            onClose,
            selectedCageTypeId,
            t,
        ]
    );

    return {
        formState,
        visibleCageTypes,
        isLocked,
        hasResolvedLockedCageType,
        isResolvingLockedCageType,
        selectedCageTypeId,
        selectedFishTypeId,
        handleFishTypeChange,
        handleCageTypeChange,
        handleCountChange,
        handleNameChange,
        handleClose,
        handleSubmit,
    };
}
